import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Map,
  APIProvider,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterViewportAlgorithm,
  type Renderer,
  type Cluster,
  type ClusterStats,
} from "@googlemaps/markerclusterer";
import DashboardPage from "../../layout";
import Loader from "../../components/Loader";
import {
  getLmdTerritoryDeliverySummary,
  type TerritoryDeliverySummaryItem,
  type DeliverySummaryDeliveryPoint,
  type LmdDeliverySummaryPayload,
} from "../../api/territoryMap";
import { getGlobalFilter } from "../../api/filters";
import {
  formatCurrentStartDate,
  formatCurrentEndDate,
  getUserInfo,
} from "../../utility";
import { delivery_man } from "../../utility/constant";
import TimeFrameSelector from "../../components/TimeFrameSelector";
import DropdownMulti from "../../components/DropdownMultiple";
import Dropdown from "../../components/Dropdown";
import Button from "../../components/Button";
import Filter from "../../assets/icons/Filter.svg";
import Close from "../../assets/close-circle.svg";
import Search from "../../assets/Search.svg";
import { useTranslation } from "react-i18next";

interface ProcessedTerritory {
  id: string;
  territory_id: string;
  name: string;
  status_active: boolean;
  coordinates: google.maps.LatLngLiteral[];
  delivered_count: number;
  pending_count: number;
  cancelled_count: number;
  total_count: number;
  center: google.maps.LatLngLiteral;
  color: string;
}

interface DeliveryPoint {
  lat: number;
  lng: number;
  status: string;
  delivery_number?: string | number;
  emp_id?: string | number;
  channel_id?: string | number;
  customer_id?: string | number;
}

interface WeightedPoint {
  lat: number;
  lng: number;
  weight: number;
}

const saudiCenter = { lat: 24.774265, lng: 46.738586 };
const saudiBounds = {
  north: 33.0,
  south: 16.0,
  east: 60.0,
  west: 34.0,
};

const GRID_PRECISION = 3;

const clusterPoints = (points: { lat: number; lng: number }[]): WeightedPoint[] => {
  const grid: Record<string, { lat: number; lng: number; count: number }> = {};
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const key = `${p.lat.toFixed(GRID_PRECISION)},${p.lng.toFixed(GRID_PRECISION)}`;
    const existing = grid[key];
    if (existing) {
      existing.lat += p.lat;
      existing.lng += p.lng;
      existing.count++;
    } else {
      grid[key] = { lat: p.lat, lng: p.lng, count: 1 };
    }
  }
  const result: WeightedPoint[] = [];
  const keys = Object.keys(grid);
  for (let i = 0; i < keys.length; i++) {
    const v = grid[keys[i]];
    result.push({ lat: v.lat / v.count, lng: v.lng / v.count, weight: v.count });
  }
  return result;
};

const STATUS_COLORS: Record<string, { label: string; gradient: string[]; dot: string; bg: string; text: string }> = {
  DELIVERED: {
    label: "Delivered",
    gradient: [
      "rgba(72, 199, 142, 0)",
      "rgba(72, 199, 142, 0.3)",
      "rgba(52, 211, 153, 0.5)",
      "rgba(34, 197, 94, 0.65)",
      "rgba(22, 163, 74, 0.8)",
      "rgba(22, 163, 74, 1)",
    ],
    dot: "#22c55e",
    bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
    text: "text-emerald-700",
  },
  PENDING: {
    label: "Pending",
    gradient: [
      "rgba(253, 224, 71, 0)",
      "rgba(253, 224, 71, 0.3)",
      "rgba(250, 204, 21, 0.5)",
      "rgba(234, 179, 8, 0.65)",
      "rgba(202, 138, 4, 0.8)",
      "rgba(202, 138, 4, 1)",
    ],
    dot: "#eab308",
    bg: "bg-amber-50 text-amber-700 border-amber-200",
    text: "text-amber-700",
  },
  CANCELLED: {
    label: "Cancelled",
    gradient: [
      "rgba(248, 113, 113, 0)",
      "rgba(248, 113, 113, 0.3)",
      "rgba(239, 68, 68, 0.5)",
      "rgba(220, 38, 38, 0.65)",
      "rgba(185, 28, 28, 0.8)",
      "rgba(185, 28, 28, 1)",
    ],
    dot: "#ef4444",
    bg: "bg-rose-50 text-rose-700 border-rose-200",
    text: "text-rose-700",
  },
};

const STATUSES = Object.keys(STATUS_COLORS) as Array<keyof typeof STATUS_COLORS>;

const PALETTE = [
  "#4285F4",
  "#EA4335",
  "#FBBC05",
  "#34A853",
  "#9C27B0",
  "#FF9800",
  "#00BCD4",
  "#E91E63",
];

function createDeliveryClusterRenderer(baseHex: string, labelColor: string): Renderer {
  return {
    render(cluster: Cluster, _stats: ClusterStats, _map: google.maps.Map) {
      const count = cluster.count;
      const position = cluster.position;
      const fillHex = baseHex;
      const o1 = 0.22;
      const o2 = 0.35;
      const o3 = 0.5;
      const o4 = 0.68;

      const size = Math.round(42 + Math.min(22, Math.log2(count + 1) * 6.5));

      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="240" height="240">
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o1}" r="130"/>
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o2}" r="110"/>
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o3}" r="90"/>
        <circle cx="120" cy="120" fill="${fillHex}" opacity="${o4}" r="72"/>
      </svg>`;
      const url = `data:image/svg+xml;base64,${btoa(svg)}`;

      return new google.maps.Marker({
        position,
        icon: { url, scaledSize: new google.maps.Size(size, size) },
        label: {
          text: String(count),
          color: labelColor,
          fontSize: size > 50 ? "13px" : "12px",
          fontWeight: "700",
        },
        zIndex: Math.min(5000, 1000 + count),
      });
    },
  };
}

const COORD_GROUP_DECIMALS = 5;

const spreadOverlappingCoordinates = (points: DeliveryPoint[]): DeliveryPoint[] => {
  if (points.length <= 1) return points;

  const groups: Record<string, DeliveryPoint[]> = {};
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const key = `${p.lat.toFixed(COORD_GROUP_DECIMALS)},${p.lng.toFixed(COORD_GROUP_DECIMALS)}`;
    const list = groups[key];
    if (list) list.push(p);
    else groups[key] = [p];
  }

  const out: DeliveryPoint[] = [];
  const groupKeys = Object.keys(groups);
  for (let g = 0; g < groupKeys.length; g++) {
    const group = groups[groupKeys[g]];
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }
    const centerLat = group[0].lat;
    const centerLng = group[0].lng;
    const n = group.length;
    const cosLat = Math.cos((centerLat * Math.PI) / 180);
    const safeCos = Math.abs(cosLat) < 1e-6 ? 1e-6 : cosLat;
    const radiusLat = 0.000035 * Math.sqrt(n);
    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      const dLat = radiusLat * Math.cos(angle);
      const dLng = (radiusLat * Math.sin(angle)) / safeCos;
      out.push({
        ...group[i],
        lat: centerLat + dLat,
        lng: centerLng + dLng,
      });
    }
  }
  return out;
};

const HEATMAP_MAX_INTENSITY = 15;
const HEATMAP_BASE_RADIUS_METERS = 2200;

const rgbaToHex = (rgba: string): string => {
  const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return "#22c55e";
  const r = Number(match[1]);
  const g = Number(match[2]);
  const b = Number(match[3]);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const getHeatmapCircleStyle = (
  weight: number,
  gradient: string[]
): { fillColor: string; fillOpacity: number } => {
  const intensity = Math.min(weight / HEATMAP_MAX_INTENSITY, 1);
  const colorIndex = Math.min(
    gradient.length - 1,
    Math.floor(intensity * (gradient.length - 1))
  );
  const rgba = gradient[colorIndex] || gradient[gradient.length - 1];
  const alphaMatch = rgba.match(/rgba?\([^)]+,\s*([\d.]+)\s*\)/);
  const baseAlpha = alphaMatch ? Number(alphaMatch[1]) : 1;
  return {
    fillColor: rgbaToHex(rgba),
    fillOpacity: Math.min(0.2 + intensity * 0.55, 0.85) * Math.max(baseAlpha, 0.35),
  };
};

const StatusHeatmapLayer: React.FC<{
  clustered: WeightedPoint[];
  gradient: string[];
  visible: boolean;
}> = ({ clustered, gradient, visible }) => {
  const map = useMap();
  const circlesRef = useRef<google.maps.Circle[]>([]);

  useEffect(() => {
    if (!map) return;

    circlesRef.current.forEach((circle) => circle.setMap(null));
    circlesRef.current = [];

    if (!visible || clustered.length === 0) {
      return;
    }

    circlesRef.current = clustered.map((point) => {
      const { fillColor, fillOpacity } = getHeatmapCircleStyle(
        point.weight,
        gradient
      );
      const radius =
        HEATMAP_BASE_RADIUS_METERS + Math.sqrt(point.weight) * 350;

      return new google.maps.Circle({
        map,
        center: { lat: point.lat, lng: point.lng },
        radius,
        fillColor,
        fillOpacity,
        strokeWeight: 0,
        clickable: false,
        zIndex: 1,
      });
    });

    return () => {
      circlesRef.current.forEach((circle) => circle.setMap(null));
      circlesRef.current = [];
    };
  }, [map, clustered, gradient, visible]);

  return null;
};

const MARKER_BUILD_CHUNK = 900;
const CLUSTER_LABEL_LIGHT = "rgba(255,255,255,0.96)";
const CLUSTER_LABEL_DARK = "rgba(24,24,27,0.94)";

const DeliveryMarkersLayer: React.FC<{
  points: DeliveryPoint[];
  statusKey: keyof typeof STATUS_COLORS;
  visible: boolean;
}> = ({ points, statusKey, visible }) => {
  const fillColor = STATUS_COLORS[statusKey].dot;
  const clusterRenderer = useMemo(
    () =>
      createDeliveryClusterRenderer(
        fillColor,
        statusKey === "PENDING" ? CLUSTER_LABEL_DARK : CLUSTER_LABEL_LIGHT
      ),
    [fillColor, statusKey]
  );
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!map) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const clusterer = clustererRef.current;
      clustererRef.current = null;
      const markers = markersRef.current;
      markersRef.current = [];
      markers.forEach((m) => {
        m.setMap(null);
      });
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      if (clusterer) {
        clusterer.setMap(null);
      }
    };

    const build = () => {
      const g = window.google?.maps;
      if (!g?.Marker) {
        timeoutId = setTimeout(build, 150);
        return;
      }
      if (cancelled) return;

      cleanup();
      if (!visible || points.length === 0) return;

      if (!map.getProjection()) {
        timeoutId = setTimeout(build, 100);
        return;
      }

      const icon: google.maps.Symbol = {
        path: g.SymbolPath.CIRCLE,
        fillColor,
        fillOpacity: 1,
        strokeOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 0.75,
        scale: 5,
      };

      if (!infoWindowRef.current) {
        infoWindowRef.current = new g.InfoWindow();
      }

      let index = 0;
      const markers: google.maps.Marker[] = [];

      const runChunk = () => {
        if (cancelled) return;
        const end = Math.min(index + MARKER_BUILD_CHUNK, points.length);
        for (; index < end; index++) {
          const p = points[index];
          const marker = new g.Marker({
            position: { lat: p.lat, lng: p.lng },
            icon,
          });

          marker.addListener("mouseover", () => {
            if (!infoWindowRef.current) return;
            infoWindowRef.current.setContent(`
              <div style="font-size:12px; line-height:1.5; min-width:180px; padding:4px;">
                <div style="font-weight:700; color:#1f2937; margin-bottom:4px; border-bottom:1px solid #e5e7eb; padding-bottom:2px;">
                  Delivery #${p.delivery_number ?? "-"}
                </div>
                <div><strong>Status:</strong> <span style="color:${STATUS_COLORS[p.status]?.dot || '#374151'}">${p.status}</span></div>
                <div><strong>Employee ID:</strong> ${p.emp_id ?? "-"}</div>
                <div><strong>Channel ID:</strong> ${p.channel_id ?? "-"}</div>
                <div><strong>Customer ID:</strong> ${p.customer_id ?? "-"}</div>
              </div>
            `);
            infoWindowRef.current.open({
              anchor: marker,
              map,
              shouldFocus: false,
            });
          });

          marker.addListener("mouseout", () => {
            infoWindowRef.current?.close();
          });

          markers.push(marker);
        }
        if (index < points.length) {
          rafRef.current = requestAnimationFrame(runChunk);
          return;
        }
        if (cancelled) {
          markers.forEach((m) => m.setMap(null));
          return;
        }
        markersRef.current = markers;
        clustererRef.current = new MarkerClusterer({
          map,
          markers,
          algorithm: new SuperClusterViewportAlgorithm({
            radius: 92,
            maxZoom: 16,
            viewportPadding: 88,
          }),
          renderer: clusterRenderer,
        });
      };

      runChunk();
    };

    build();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      cleanup();
    };
  }, [map, points, fillColor, visible, clusterRenderer]);

  return null;
};

// Helper function to calculate center of polygon
const getPolygonCenter = (coordinates: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral => {
  if (coordinates.length === 0) return { lat: 0, lng: 0 };
  let sumLat = 0;
  let sumLng = 0;
  coordinates.forEach((coord) => {
    sumLat += coord.lat;
    sumLng += coord.lng;
  });
  return {
    lat: sumLat / coordinates.length,
    lng: sumLng / coordinates.length,
  };
};

/** Territory Polygons Layer */
const TerritoryPolygonsLayer: React.FC<{
  territories: ProcessedTerritory[];
  selectedTerritoryId: string | null;
  onSelectTerritory: (id: string | null) => void;
}> = ({
  territories,
  selectedTerritoryId,
  onSelectTerritory,
}) => {
  const map = useMap();
  const polygonsRef = useRef<Record<string, google.maps.Polygon>>({});
  const tooltipDivRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map) return;

    let tooltipEl = tooltipDivRef.current;
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.className = "territory-map-tooltip";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.zIndex = "1000";
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.backgroundColor = "#ffffff";
      tooltipEl.style.color = "#1f2937";
      tooltipEl.style.padding = "8px 12px";
      tooltipEl.style.borderRadius = "6px";
      tooltipEl.style.fontSize = "12px";
      tooltipEl.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
      tooltipEl.style.display = "none";
      tooltipEl.style.border = "1px solid #e5e7eb";
      tooltipEl.style.transition = "opacity 0.15s ease";
      tooltipDivRef.current = tooltipEl;
      const mapDiv = map.getDiv();
      if (mapDiv) {
        mapDiv.style.position = "relative";
        mapDiv.appendChild(tooltipEl);
      }
    }

    // Cleanup previous polygons
    Object.values(polygonsRef.current).forEach((p) => {
      const hoverListeners = (p as any).__hoverListeners;
      if (hoverListeners) {
        hoverListeners.forEach((l: google.maps.MapsEventListener) => google.maps.event.removeListener(l));
      }
      p.setMap(null);
    });
    polygonsRef.current = {};

    const g = window.google?.maps;
    if (!g?.Polygon) return;

    territories.forEach((t) => {
      if (!t.coordinates || t.coordinates.length < 3) return;

      const isSelected = selectedTerritoryId === t.id;
      const isInactive = !t.status_active;
      const baseColor = isInactive ? "#9CA3AF" : t.color;

      const polygon = new g.Polygon({
        paths: t.coordinates,
        fillColor: baseColor,
        fillOpacity: isSelected ? 0.55 : isInactive ? 0.2 : 0.35,
        strokeWeight: isSelected ? 4 : 2,
        strokeColor: isSelected ? "#4338CA" : baseColor,
        clickable: true,
        zIndex: isSelected ? 20 : 2,
        map,
      });

      polygon.addListener("click", (e: google.maps.MapMouseEvent) => {
        e.stop();
        onSelectTerritory(isSelected ? null : t.id);
      });

      // Hover tooltip: Territory ID, Name, Delivered, Pending (No total, No cancelled)
      const tooltipContent = `
        <div style="font-weight: 700; margin-bottom: 2px; font-size: 12px; color: #111827;">${(t.name || "Territory").replace(/</g, "&lt;")}</div>
        <div style="font-size: 11px; color: #6B7280; margin-bottom: 6px;"><strong>ID:</strong> ${t.territory_id}</div>
        <div style="display: flex; gap: 12px; font-size: 11px; border-top: 1px solid #E5E7EB; padding-top: 4px;">
          <span style="color: #16A34A;"><strong>Delivered:</strong> ${t.delivered_count}</span>
          <span style="color: #D97706;"><strong>Pending:</strong> ${t.pending_count}</span>
        </div>
      `;

      const mouseoverListener = polygon.addListener("mouseover", (e: google.maps.MapMouseEvent) => {
        if (!tooltipEl) return;
        tooltipEl.innerHTML = tooltipContent;
        const domEvent = (e as any).domEvent as MouseEvent | undefined;
        const mapDiv = map.getDiv();
        if (domEvent && mapDiv) {
          const rect = mapDiv.getBoundingClientRect();
          tooltipEl.style.left = `${domEvent.clientX - rect.left + 14}px`;
          tooltipEl.style.top = `${domEvent.clientY - rect.top + 14}px`;
        }
        tooltipEl.style.display = "block";
        polygon.setOptions({ fillOpacity: isSelected ? 0.65 : 0.5 });
      });

      const mouseoutListener = polygon.addListener("mouseout", () => {
        if (tooltipEl) tooltipEl.style.display = "none";
        polygon.setOptions({ fillOpacity: isSelected ? 0.55 : isInactive ? 0.2 : 0.35 });
      });

      (polygon as any).__hoverListeners = [mouseoverListener, mouseoutListener];
      polygonsRef.current[t.id] = polygon;
    });

    return () => {
      if (tooltipDivRef.current) {
        tooltipDivRef.current.style.display = "none";
        if (tooltipDivRef.current.parentNode) {
          tooltipDivRef.current.parentNode.removeChild(tooltipDivRef.current);
        }
        tooltipDivRef.current = null;
      }
      Object.values(polygonsRef.current).forEach((polygon) => {
        const hoverListeners = (polygon as any).__hoverListeners;
        if (hoverListeners) {
          hoverListeners.forEach((l: google.maps.MapsEventListener) => google.maps.event.removeListener(l));
        }
        polygon.setMap(null);
      });
      polygonsRef.current = {};
    };
  }, [map, territories, selectedTerritoryId, onSelectTerritory]);

  return null;
};

/** Fit map bounds helper */
const MapBoundsController: React.FC<{
  territories: ProcessedTerritory[];
  deliveries: DeliveryPoint[];
  selectedTerritoryId: string | null;
}> = ({ territories, deliveries, selectedTerritoryId }) => {
  const map = useMap();
  const initialFitDone = useRef(false);

  useEffect(() => {
    if (!map) return;

    if (selectedTerritoryId) {
      const target = territories.find((t) => t.id === selectedTerritoryId);
      if (target && target.coordinates.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        target.coordinates.forEach((c) => bounds.extend(c));
        map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
      }
      return;
    }

    if (!initialFitDone.current && (territories.length > 0 || deliveries.length > 0)) {
      const bounds = new google.maps.LatLngBounds();
      let hasPoints = false;
      territories.forEach((t) => {
        t.coordinates.forEach((c) => {
          bounds.extend(c);
          hasPoints = true;
        });
      });
      deliveries.slice(0, 500).forEach((d) => {
        bounds.extend({ lat: d.lat, lng: d.lng });
        hasPoints = true;
      });

      if (hasPoints) {
        map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
        initialFitDone.current = true;
      }
    }
  }, [map, territories, deliveries, selectedTerritoryId]);

  return null;
};

const formatDateForInput = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getCurrentDate = () => formatDateForInput(new Date());
const getMinSelectableDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return formatDateForInput(d);
};

const normalizeStatusFilterFlags = (status: string) => {
  const s = String(status || "PENDING").toUpperCase();
  return {
    filter_by_created_at: s === "PENDING",
    filter_by_delivered_at: s === "DELIVERED",
    filter_by_rejected_at: false,
    filter_by_cancelled_at: s === "CANCELLED",
  };
};

const clampDateToRange = (value: string, min: string, max: string): string => {
  if (!value) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const LmdSummary: React.FC = () => {
  const { t } = useTranslation();
  const today = getCurrentDate();
  const minSelectableDate = getMinSelectableDate();

  const [loading, setLoading] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Global filters options
  const [globalFilters, setGlobalFilters] = useState<any>(null);

  // Filter form states
  const [selectedRegion, setRegion] = useState<any>([]);
  const [selectedSalesOffice, setSelectedSalesOffice] = useState<any>([]);
  const [channelId, setChannelId] = useState<any>([]);
  const [deliveryStatus, setDeliveryStatus] = useState<string>("PENDING");
  const [startDate, setStartDate] = useState<any>(today);
  const [endDate, setEndDate] = useState<any>(today);
  const [timeFrame, setSelectTimeFrame] = useState<any>("Today");

  // Map view layer controls
  const [deliveryLayerMode, setDeliveryLayerMode] = useState<"points" | "heatmap" | "none">("points");
  const [territoryStatusFilter, setTerritoryStatusFilter] = useState<"all" | "active" | "inactive">("all");

  // Search & sorting for territories sidebar
  const [territorySearch, setTerritorySearch] = useState("");
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | null>(null);

  // Data states
  const [activeSalesOfficeId, setActiveSalesOfficeId] = useState<string>("");
  const [rawTerritories, setRawTerritories] = useState<TerritoryDeliverySummaryItem[]>([]);
  const [rawDeliveries, setRawDeliveries] = useState<DeliverySummaryDeliveryPoint[]>([]);
  const [totalTerritoryCount, setTotalTerritoryCount] = useState(0);

  // Get global filters from API
  const getGlobalFilters = async (data: any) => {
    try {
      setLoadingFilter(true);
      const response = await getGlobalFilter(data);
      setLoadingFilter(false);
      setGlobalFilters(response);

      // On initial mount, set default activeSalesOfficeId if none is set yet
      if (!activeSalesOfficeId && response?.sales_offices?.length > 0) {
        const firstOfficeId = String(response.sales_offices[0].id);
        setActiveSalesOfficeId(firstOfficeId);
      }
    } catch (error) {
      console.error("Error fetching global filters:", error);
      setLoadingFilter(false);
    }
  };

  const buildFilterPayload = useCallback(() => {
    const data: any = {};
    if (selectedRegion.length > 0) data["regions"] = selectedRegion;
    if (selectedSalesOffice.length > 0) data["sales_office_ids"] = selectedSalesOffice;
    if (channelId.length > 0) data["channel_ids"] = channelId;
    if (deliveryStatus) data["status"] = deliveryStatus;
    Object.assign(data, normalizeStatusFilterFlags(deliveryStatus));
    data["endpoint"] = "visit-summary";
    data["start_date"] = formatCurrentStartDate(new Date(startDate));
    data["end_date"] = formatCurrentEndDate(new Date(endDate));
    data["positions"] = [delivery_man];
    return data;
  }, [selectedRegion, selectedSalesOffice, channelId, deliveryStatus, startDate, endDate]);

  const handleFiltersUpdate = () => {
    getGlobalFilters(buildFilterPayload());
  };

  useEffect(() => {
    getGlobalFilters(buildFilterPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedSalesOffice([]);
    setChannelId([]);
  }, [selectedRegion]);

  useEffect(() => {
    setChannelId([]);
  }, [selectedSalesOffice]);

  // Fetch Delivery Summary Data
  const fetchData = async (officeIdToUse?: string, statusFilter?: string) => {
    const officeId = officeIdToUse || (selectedSalesOffice.length > 0 ? String(selectedSalesOffice[0]) : activeSalesOfficeId);
    if (!officeId) {
      return;
    }
    setActiveSalesOfficeId(officeId);
    setLoading(true);
    setSelectedTerritoryId(null);

    try {
      const activeStatus = statusFilter || deliveryStatus || "PENDING";
      const filterFlags = normalizeStatusFilterFlags(activeStatus);

      const payload: LmdDeliverySummaryPayload = {
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate + "T23:59:59.999Z").toISOString(),
        filter_by_created_at: filterFlags.filter_by_created_at,
        filter_by_delivered_at: filterFlags.filter_by_delivered_at,
        filter_by_rejected_at: filterFlags.filter_by_rejected_at,
        filter_by_cancelled_at: filterFlags.filter_by_cancelled_at,
      };

      const userInfo = getUserInfo();
      if (userInfo?.emp_id) {
        payload.supervisor_id = String(userInfo.emp_id);
      }

      if (channelId.length > 0) {
        payload.channel_ids = channelId.map((id: any) => String(id));
      }

      const res = await getLmdTerritoryDeliverySummary(officeId, payload);
      const data = res?.data || res || {};

      const territoriesList = data.territories || [];
      const deliveriesList = data.deliveries || [];

      setRawTerritories(territoriesList);
      setRawDeliveries(deliveriesList);
      setTotalTerritoryCount(data.total ?? territoriesList.length);
    } catch (err) {
      console.error("Failed to fetch LMD delivery summary:", err);
      setRawTerritories([]);
      setRawDeliveries([]);
      setTotalTerritoryCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial load when activeSalesOfficeId is resolved
  useEffect(() => {
    if (activeSalesOfficeId) {
      fetchData(activeSalesOfficeId, deliveryStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSalesOfficeId]);

  // Process territories
  const processedTerritories = useMemo((): ProcessedTerritory[] => {
    return rawTerritories
      .filter((t) => {
        if (!t || !t.coordinates || !Array.isArray(t.coordinates)) return false;
        if (territoryStatusFilter === "active" && t.status_active === false) return false;
        if (territoryStatusFilter === "inactive" && t.status_active === true) return false;
        return true;
      })
      .map((t, idx) => {
        const coordinates: google.maps.LatLngLiteral[] = t.coordinates
          .map((coord: any) => {
            const lat = typeof coord.lat === "number" ? coord.lat : parseFloat(coord.lat);
            const lng = typeof coord.lng === "number" ? coord.lng : parseFloat(coord.lng);
            if (isNaN(lat) || isNaN(lng)) return null;
            return { lat, lng };
          })
          .filter((c): c is google.maps.LatLngLiteral => c !== null);

        const center = getPolygonCenter(coordinates);
        const delivered = Number(t.delivered_count ?? 0);
        const pending = Number(t.pending_count ?? 0);
        const cancelled = Number(t.cancelled_count ?? 0);
        const total = Number(t.total_count ?? delivered + pending + cancelled);

        return {
          id: `territory-${t.territory_id ?? idx}`,
          territory_id: String(t.territory_id ?? idx),
          name: t.territory_name || `Territory ${t.territory_id ?? idx + 1}`,
          status_active: t.status_active !== false,
          coordinates,
          delivered_count: delivered,
          pending_count: pending,
          cancelled_count: cancelled,
          total_count: total,
          center,
          color: PALETTE[idx % PALETTE.length],
        };
      });
  }, [rawTerritories, territoryStatusFilter]);

  // Filtered territories list for sidebar
  const sidebarTerritories = useMemo(() => {
    let list = [...processedTerritories];
    if (territorySearch.trim()) {
      const q = territorySearch.toLowerCase().trim();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.territory_id.toLowerCase().includes(q)
      );
    }
    return list;
  }, [processedTerritories, territorySearch]);

  // Process delivery points
  const { allPoints, statusCounts } = useMemo(() => {
    const points: DeliveryPoint[] = [];
    const counts: Record<string, number> = { DELIVERED: 0, CANCELLED: 0, PENDING: 0 };

    rawDeliveries.forEach((delivery: any) => {
      const latVal = delivery.latitude ?? delivery.lat ?? delivery.delivered_latitude;
      const lngVal = delivery.longitude ?? delivery.lng ?? delivery.delivered_longitude;
      const status = (delivery.status || "PENDING").toUpperCase();

      if (latVal == null || lngVal == null || latVal === "" || lngVal === "") {
        if (counts[status] !== undefined) counts[status]++;
        return;
      }

      const lat = typeof latVal === "number" ? latVal : parseFloat(latVal);
      const lng = typeof lngVal === "number" ? lngVal : parseFloat(lngVal);

      if (!isNaN(lat) && !isNaN(lng)) {
        points.push({
          lat,
          lng,
          status,
          delivery_number: delivery.delivery_number,
          emp_id: delivery.emp_id,
          channel_id: delivery.channel_id,
          customer_id: delivery.customer_id,
        });
        if (counts[status] !== undefined) counts[status]++;
      }
    });

    return { allPoints: points, statusCounts: counts };
  }, [rawDeliveries]);

  const pointsByStatus = useMemo(() => {
    const result: Record<string, DeliveryPoint[]> = {};
    for (const status of STATUSES) {
      const raw = allPoints.filter((p) => p.status === status);
      result[status] = spreadOverlappingCoordinates(raw);
    }
    return result;
  }, [allPoints]);

  const clusteredByStatus = useMemo(() => {
    const result: Record<string, WeightedPoint[]> = {};
    for (const status of STATUSES) {
      const raw = allPoints.filter((p) => p.status === status);
      result[status] = clusterPoints(raw);
    }
    return result;
  }, [allPoints]);

  const displayedStatuses = useMemo((): Array<keyof typeof STATUS_COLORS> => {
    const s = deliveryStatus;
    if (s === "DELIVERED" || s === "CANCELLED" || s === "PENDING") return [s];
    return STATUSES;
  }, [deliveryStatus]);

  const handleApplyFilters = () => {
    const primaryOffice = selectedSalesOffice.length > 0
      ? String(selectedSalesOffice[0])
      : (activeSalesOfficeId || (globalFilters?.sales_offices?.[0]?.id ? String(globalFilters.sales_offices[0].id) : ""));
    if (primaryOffice) {
      fetchData(primaryOffice, deliveryStatus);
    }
    setFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    setRegion([]);
    setSelectedSalesOffice([]);
    setChannelId([]);
    setDeliveryStatus("PENDING");
    setStartDate(getCurrentDate());
    setEndDate(getCurrentDate());
    setSelectTimeFrame("Today");
    setFilterDrawerOpen(false);
    const defaultOffice = globalFilters?.sales_offices?.[0]?.id ? String(globalFilters.sales_offices[0].id) : "";
    if (defaultOffice) {
      setActiveSalesOfficeId(defaultOffice);
      fetchData(defaultOffice, "PENDING");
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextStart = clampDateToRange(e.target.value, minSelectableDate, today);
    setStartDate(nextStart);
    if (endDate && endDate < nextStart) {
      setEndDate(nextStart);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentStart = clampDateToRange(String(startDate), minSelectableDate, today);
    const nextEnd = clampDateToRange(e.target.value, currentStart, today);
    setEndDate(nextEnd);
  };

  return (
    <DashboardPage>
      <div className="flex flex-col h-[calc(100vh-54px)] overflow-hidden bg-[#f8f9fa]">
        {/* Top Header Bar */}
        <div className="bg-white px-4 py-3 border-b border-gray-300 flex justify-between items-center z-10 shrink-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-800">
              {t("Landing.lmd", "LMD")} {t("masterList.summary", "Summary")}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setFilterDrawerOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-13 font-normal rounded transition-all hover:bg-gray-50 border border-gray-200"
            >
              <img src={Filter} alt="Filter" className="w-4 h-4 mr-2" />
              {t("planogram.filter")}
            </button>
          </div>
        </div>

        {/* Main Content Split: Left Sidebar + Right Map */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Left Territory Breakdown Sidebar */}
          <div
            className={`transition-all duration-300 ease-in-out bg-white border-r border-gray-300 flex flex-col z-10 ${
              isSidebarOpen ? "w-full md:w-[38%] xl:w-[32%]" : "w-0 hidden md:hidden"
            } h-full overflow-hidden shrink-0`}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-300 bg-white">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <img src={Search} alt="Search" className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  value={territorySearch}
                  onChange={(e) => setTerritorySearch(e.target.value)}
                  placeholder={t("planogram.searchPlaceholder") || "Search territories..."}
                  className="w-full pl-9 pr-3 py-2 text-13 border border-gray-300 rounded text-gray-700 placeholder-gray-400 focus:outline-none focus:border-purple focus:ring-1 focus:ring-purple"
                />
              </div>
            </div>

            {/* Territory Table header count */}
            <div className="px-3.5 py-2 bg-gray-100 border-b border-gray-300 flex justify-between items-center text-12 text-gray-600">
              <span className="font-bold text-gray-700">
                {t("Landing.territory", "Territories")}
              </span>
              <span className="text-12 text-gray-500 font-medium">
                {sidebarTerritories.length} / {totalTerritoryCount}
              </span>
            </div>

            {/* Territories Table */}
            <div className="flex-1 overflow-y-auto scrollbar scrollbar-thin">
              {loading ? (
                <div className="py-16 flex items-center justify-center">
                  <Loader />
                </div>
              ) : sidebarTerritories.length > 0 ? (
                <table className="min-w-full bg-white border-separate border-spacing-0 border-b border-gray-300 text-left">
                  <thead className="bg-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="p-2.5 border-b border-r border-gray-300 text-12 font-bold text-gray-700 w-16">
                        ID
                      </th>
                      <th className="p-2.5 border-b border-r border-gray-300 text-12 font-bold text-gray-700 min-w-[120px]">
                        Name
                      </th>
                      <th className="p-2.5 border-b border-r border-gray-300 text-12 font-bold text-gray-700 text-center w-20">
                        Delivered
                      </th>
                      <th className="p-2.5 border-b border-gray-300 text-12 font-bold text-gray-700 text-center w-20">
                        Pending
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sidebarTerritories.map((territory) => {
                      const isSelected = selectedTerritoryId === territory.id;
                      return (
                        <tr
                          key={territory.id}
                          onClick={() =>
                            setSelectedTerritoryId(isSelected ? null : territory.id)
                          }
                          className={`border-b border-gray-200 text-12 cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-[#F3E6F4] font-semibold text-purple"
                              : "hover:bg-gray-50 text-gray-800"
                          }`}
                        >
                          <td className="p-2.5 font-mono border-b border-r border-gray-200">
                            {territory.territory_id}
                          </td>
                          <td className="p-2.5 border-b border-r border-gray-200">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0 border border-black/10"
                                style={{ backgroundColor: territory.color }}
                              />
                              <span className="truncate max-w-[130px]" title={territory.name}>
                                {territory.name}
                              </span>
                            </div>
                          </td>
                          <td className="p-2.5 text-center border-b border-r border-gray-200 font-bold text-customgreen">
                            {territory.delivered_count}
                          </td>
                          <td className="p-2.5 text-center border-b border-gray-200 font-bold text-yellow">
                            {territory.pending_count}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-16 text-gray-400 text-13">
                  No territories found.
                </div>
              )}
            </div>
          </div>

          {/* Map Container */}
          <div className="relative transition-all duration-300 ease-in-out flex-1 h-full overflow-hidden">
            {/* Desktop Minimize/Maximize Button */}
            <div className="hidden md:block">
              <button
                onClick={() => setIsSidebarOpen((v) => !v)}
                className="absolute top-1/2 z-20 px-1.5 py-6 bg-[#D099D4] text-white text-14 font-bold transform -translate-y-1/2 transition-all duration-300 ease-in-out hover:scale-105 hover:bg-purple focus:outline-none flex items-center justify-center shadow-md rounded-r-md left-0"
                title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
              >
                <span>{isSidebarOpen ? "‹" : "›"}</span>
              </button>
            </div>

            {/* Top Left Stats Bar over map */}
            <div className="absolute top-4 left-6 z-20 hidden sm:flex items-center gap-3 bg-white/95 backdrop-blur-sm border border-gray-300 rounded shadow-md px-3.5 py-2 text-12 font-medium">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-customgreen" />
                <span className="text-gray-600">Delivered:</span>
                <span className="font-bold text-customgreen">{statusCounts.DELIVERED ?? 0}</span>
              </div>
              <span className="text-gray-300">|</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-yellow" />
                <span className="text-gray-600">Pending:</span>
                <span className="font-bold text-yellow">{statusCounts.PENDING ?? 0}</span>
              </div>
              <span className="text-gray-300">|</span>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-600">Total:</span>
                <span className="font-bold text-gray-800">{rawDeliveries.length}</span>
              </div>
            </div>

            {/* Top Right Controls on Map */}
            <div className="absolute top-4 right-4 z-20 flex items-center gap-2 flex-wrap">
              {/* Delivery Layer Toggle Button Group */}
              <div className="flex items-center bg-white border border-gray-300 rounded shadow-md overflow-hidden text-12 font-medium">
                <button
                  type="button"
                  onClick={() => setDeliveryLayerMode("points")}
                  className={`px-3 py-1.5 transition-colors ${
                    deliveryLayerMode === "points"
                      ? "bg-purple text-white font-semibold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Points
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryLayerMode("heatmap")}
                  className={`px-3 py-1.5 transition-colors border-l border-gray-200 ${
                    deliveryLayerMode === "heatmap"
                      ? "bg-purple text-white font-semibold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Heatmap
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryLayerMode("none")}
                  className={`px-3 py-1.5 transition-colors border-l border-gray-200 ${
                    deliveryLayerMode === "none"
                      ? "bg-gray-700 text-white font-semibold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Off
                </button>
              </div>

              {/* Territory Status Filter */}
              <div className="min-w-[140px] shadow-md rounded">
                <Dropdown
                  label=""
                  selectedValue={territoryStatusFilter}
                  onChange={(val) => {
                    setTerritoryStatusFilter((String(val) || "all") as any);
                  }}
                  options={[
                    { value: "all", label: "All territories" },
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  placeholder="Status"
                  searchable={false}
                />
              </div>
            </div>

            {loading && (
              <div className="absolute inset-0 z-30 bg-white/60 flex items-center justify-center">
                <Loader />
              </div>
            )}

            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY || ""}>
              <Map
                style={{ width: "100%", height: "100%" }}
                defaultCenter={saudiCenter}
                defaultZoom={7}
                gestureHandling="greedy"
                disableDefaultUI={true}
                zoomControl={true}
                minZoom={6}
                maxZoom={18}
                restriction={{
                  latLngBounds: saudiBounds,
                  strictBounds: true,
                }}
              >
                <MapBoundsController
                  territories={processedTerritories}
                  deliveries={allPoints}
                  selectedTerritoryId={selectedTerritoryId}
                />

                {/* Territory Polygons Layer */}
                <TerritoryPolygonsLayer
                  territories={processedTerritories}
                  selectedTerritoryId={selectedTerritoryId}
                  onSelectTerritory={setSelectedTerritoryId}
                />

                {/* Delivery Layer */}
                {deliveryLayerMode === "points" &&
                  displayedStatuses.map((status) => (
                    <DeliveryMarkersLayer
                      key={`pts-${status}`}
                      statusKey={status}
                      points={pointsByStatus[status] || []}
                      visible={true}
                    />
                  ))}

                {deliveryLayerMode === "heatmap" &&
                  displayedStatuses.map((status) => (
                    <StatusHeatmapLayer
                      key={`hm-${status}`}
                      clustered={clusteredByStatus[status] || []}
                      gradient={STATUS_COLORS[status].gradient}
                      visible={true}
                    />
                  ))}
              </Map>
            </APIProvider>
          </div>
        </div>

        {/* Filter Slide-in Drawer */}
        {filterDrawerOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end sm:items-start items-center z-50">
            <div className="relative bg-white shadow-lg w-full max-w-md h-full">
              <div className="absolute z-30 top-[50%] left-[45%]">
                {loadingFilter && <Loader />}
              </div>
              <div className="flex justify-between p-6 border-b-2">
                <div className="text-16 font-bold">
                  {t("visitreports.filters.title")}
                </div>
                <div>
                  <img
                    className="cursor-pointer"
                    onClick={() => setFilterDrawerOpen(false)}
                    src={Close}
                    alt=""
                  />
                </div>
              </div>
              <div className="flex flex-col justify-between h-[calc(100vh-100px)] px-12 pt-6 overflow-y-auto scrollbar-thin">
                <div className="flex flex-col gap-[30px]">
                  <div className="flex space-x-4">
                    <div className="w-1/2">
                      <DropdownMulti
                        label={t("visitreports.filters.region")}
                        options={globalFilters?.regions?.map((r: any) => ({
                          value: r,
                          label: r,
                        })) || []}
                        selectedValues={selectedRegion}
                        onClose={handleFiltersUpdate}
                        onChange={setRegion}
                        placeholder={t("visitreports.filters.selectRegion")}
                        showSelectAll={true}
                      />
                    </div>
                    <div className="w-1/2">
                      <DropdownMulti
                        label={t("visitreports.filters.salesOffice")}
                        options={
                          globalFilters?.sales_offices?.map((s: any) => ({
                            value: s.id,
                            label: s.name,
                          })) || []
                        }
                        selectedValues={selectedSalesOffice}
                        onClose={handleFiltersUpdate}
                        onChange={setSelectedSalesOffice}
                        disabled={loadingFilter}
                        placeholder={t("visitreports.filters.selectSalesOffice")}
                        showSelectAll={true}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-full">
                      <DropdownMulti
                        label={t("visitreports.filters.channelID")}
                        options={
                          globalFilters?.channels?.map((c: any) => ({
                            value: c.id,
                            label: c.name,
                          })) || []
                        }
                        selectedValues={channelId}
                        onClose={handleFiltersUpdate}
                        onChange={setChannelId}
                        disabled={loadingFilter}
                        placeholder={t("visitreports.filters.selectChannelID")}
                      />
                    </div>
                  </div>

                  <div className="w-full">
                    <Dropdown
                      label={t("visitreports.filters.deliverystatus")}
                      options={[
                        { value: "DELIVERED", label: "Delivered" },
                        { value: "CANCELLED", label: "Cancelled" },
                        { value: "PENDING", label: "Pending" },
                      ]}
                      selectedValue={deliveryStatus}
                      onChange={(v) => setDeliveryStatus(String(v))}
                      placeholder={t("visitreports.filters.deliverystatus")}
                    />
                  </div>

                  <div className="date-range-picker space-y-2 p-1 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-14 font-semibold text-gray-700">
                        {t("visitreports.filters.date")}:
                      </label>
                    </div>
                    <div className="flex space-x-4 justify-between w-full">
                      <div className="w-full">
                        <label className="block text-10 text-gray-700 mb-1">
                          {t("visitreports.filters.startDate")}:
                        </label>
                        <input
                          type="date"
                          className="rounded p-2 border-2 w-full border-gray-400"
                          value={typeof startDate === "string" ? startDate : formatDateForInput(new Date(startDate))}
                          onChange={handleStartDateChange}
                          min={minSelectableDate}
                          max={today}
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </div>
                      <div className="w-full">
                        <label className="block text-10 text-gray-700 mb-1">
                          {t("visitreports.filters.endDate")}:
                        </label>
                        <input
                          type="date"
                          className="rounded p-2 border-2 w-full border-gray-400"
                          value={typeof endDate === "string" ? endDate : formatDateForInput(new Date(endDate))}
                          onChange={handleEndDateChange}
                          min={typeof startDate === "string" ? startDate : formatDateForInput(new Date(startDate))}
                          max={today}
                          onKeyDown={(e) => e.preventDefault()}
                        />
                      </div>
                    </div>
                    <div>
                      <TimeFrameSelector
                        customClass="w-full border-2 rounded p-2 border-gray-300"
                        setSelectTimeFrame={setSelectTimeFrame}
                        timeFrame={timeFrame}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-4 pb-6">
                  <Button
                    onClick={handleClearFilters}
                    label={t("visitreports.filters.clearFilter")}
                    type="grey"
                  />
                  <Button
                    onClick={handleApplyFilters}
                    label={t("visitreports.filters.applyFilter")}
                    type="purple"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardPage>
  );
};

export default LmdSummary;
